import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  AlertCircle,
  Loader2,
  RefreshCw,
  Search,
  X,
  Package,
  Layers,
  DollarSign,
  TrendingUp,
  Calendar,
} from 'lucide-react';
import { ReportLayout } from '../../components/reports/ReportLayout';
import SortableTableHeader from '../../components/SortableTableHeader';
import AsyncItemSelect from '../../components/AsyncItemSelect';
import InventoryMovementTotalsBar from '../../components/transactions/InventoryMovementTotalsBar';
import { getBankCollateralStock, getStockValuation } from '../../utils/reportApi';
import { ApiValidationError } from '../../types/auth';
import type {
  BankCollateralStockItem,
  BankCollateralStockReport,
  StockValuationLayer,
} from '../../types/report';
import type { Item } from '../../types/item';
import { formatCurrency, formatQuantity } from '../../utils/formatCurrency';
import { todayLocalIso } from '../../utils/localDate';
import {
  bankCollateralRowKey,
  computeBankCollateralTotals,
} from '../../utils/bankCollateralSelection';

type SortColumn =
  | 'item'
  | 'eligible_qty'
  | 'extended_cost'
  | 'extended_retail'
  | 'gross_margin'
  | 'margin_percent';

const formatLabel = (value: string) =>
  value
    .toLowerCase()
    .split('_')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');

const formatReportDate = (dateString: string) =>
  new Date(dateString).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });

const formatMarginPercent = (value: string, extendedRetail: string) => {
  const retail = parseFloat(extendedRetail) || 0;
  if (retail === 0) return '—';
  const pct = parseFloat(value);
  return Number.isFinite(pct) ? `${pct.toFixed(2)}%` : '—';
};

const BankCollateralStock = () => {
  const navigate = useNavigate();

  const [filterItemId, setFilterItemId] = useState<number | null>(null);
  const [filterItemName, setFilterItemName] = useState('');
  const [activeOnly, setActiveOnly] = useState(true);
  const [includeZeroQty, setIncludeZeroQty] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  const [report, setReport] = useState<BankCollateralStockReport | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [sortColumn, setSortColumn] = useState<SortColumn>('item');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc');

  const [selectedKeys, setSelectedKeys] = useState<Set<string>>(() => new Set());
  const selectAllCheckboxRef = useRef<HTMLInputElement>(null);

  const [expandedLayers, setExpandedLayers] = useState<Record<number, boolean>>({});
  const [layerData, setLayerData] = useState<Record<number, StockValuationLayer[]>>({});
  const [layerLoading, setLayerLoading] = useState<Record<number, boolean>>({});
  const [layerErrors, setLayerErrors] = useState<Record<number, string>>({});

  const fetchReport = useCallback(
    async (params: {
      item_id?: number;
      active_only: boolean;
      include_zero_qty: boolean;
    }) => {
      try {
        setLoading(true);
        setError(null);
        const data = await getBankCollateralStock({
          item_id: params.item_id,
          active_only: params.active_only,
          include_zero_qty: params.include_zero_qty,
        });
        setReport(data);
        setExpandedLayers({});
        setLayerData({});
        setLayerErrors({});
      } catch (err) {
        if (err instanceof ApiValidationError) {
          const firstError = Object.values(err.errors).flat()[0];
          setError(firstError || err.message);
        } else {
          setError(err instanceof Error ? err.message : 'Failed to load report');
        }
        setReport(null);
      } finally {
        setLoading(false);
      }
    },
    []
  );

  const runReport = useCallback(() => {
    void fetchReport({
      item_id: filterItemId ?? undefined,
      active_only: activeOnly,
      include_zero_qty: includeZeroQty,
    });
  }, [fetchReport, filterItemId, activeOnly, includeZeroQty]);

  useEffect(() => {
    void fetchReport({
      active_only: true,
      include_zero_qty: false,
    });
  }, [fetchReport]);

  const handleSort = (column: SortColumn) => {
    if (sortColumn === column) {
      setSortDir(sortDir === 'asc' ? 'desc' : 'asc');
    } else {
      setSortColumn(column);
      setSortDir(column === 'item' ? 'asc' : 'desc');
    }
  };

  const filteredItems = useMemo(() => {
    const items = report?.items ?? [];
    if (!searchQuery.trim()) return items;
    const query = searchQuery.toLowerCase();
    return items.filter(
      (item) =>
        item.sku.toLowerCase().includes(query) || item.name.toLowerCase().includes(query)
    );
  }, [report?.items, searchQuery]);

  const sortedItems = useMemo(() => {
    return [...filteredItems].sort((a, b) => {
      let comparison = 0;
      if (sortColumn === 'item') {
        comparison = a.name.localeCompare(b.name);
      } else if (sortColumn === 'eligible_qty') {
        comparison = parseFloat(a.eligible_qty) - parseFloat(b.eligible_qty);
      } else if (sortColumn === 'extended_cost') {
        comparison = parseFloat(a.extended_cost) - parseFloat(b.extended_cost);
      } else if (sortColumn === 'extended_retail') {
        comparison = parseFloat(a.extended_retail) - parseFloat(b.extended_retail);
      } else if (sortColumn === 'gross_margin') {
        comparison = parseFloat(a.gross_margin) - parseFloat(b.gross_margin);
      } else if (sortColumn === 'margin_percent') {
        comparison = parseFloat(a.margin_percent) - parseFloat(b.margin_percent);
      }
      return sortDir === 'asc' ? comparison : -comparison;
    });
  }, [filteredItems, sortColumn, sortDir]);

  const pageTotals = useMemo(() => computeBankCollateralTotals(filteredItems), [filteredItems]);

  const visibleRowKeys = useMemo(
    () => filteredItems.map(bankCollateralRowKey),
    [filteredItems]
  );

  const selectedRows = useMemo(
    () => filteredItems.filter((item) => selectedKeys.has(bankCollateralRowKey(item))),
    [filteredItems, selectedKeys]
  );

  const selectedTotals = useMemo(
    () => computeBankCollateralTotals(selectedRows),
    [selectedRows]
  );

  const allPageSelected =
    visibleRowKeys.length > 0 && visibleRowKeys.every((key) => selectedKeys.has(key));

  const somePageSelected = visibleRowKeys.some((key) => selectedKeys.has(key));

  const toggleRow = (key: string, checked: boolean) => {
    setSelectedKeys((prev) => {
      const next = new Set(prev);
      if (checked) next.add(key);
      else next.delete(key);
      return next;
    });
  };

  const toggleSelectAll = (checked: boolean) => {
    setSelectedKeys((prev) => {
      const next = new Set(prev);
      if (checked) {
        for (const key of visibleRowKeys) next.add(key);
      } else {
        for (const key of visibleRowKeys) next.delete(key);
      }
      return next;
    });
  };

  const clearSelection = () => setSelectedKeys(new Set());

  useEffect(() => {
    setSelectedKeys(new Set());
  }, [searchQuery, filterItemId, activeOnly, includeZeroQty, report]);

  useEffect(() => {
    const visibleKeySet = new Set(visibleRowKeys);
    setSelectedKeys((prev) => {
      const pruned = new Set([...prev].filter((key) => visibleKeySet.has(key)));
      return pruned.size === prev.size ? prev : pruned;
    });
  }, [visibleRowKeys]);

  useEffect(() => {
    const input = selectAllCheckboxRef.current;
    if (input) {
      input.indeterminate = somePageSelected && !allPageSelected;
    }
  }, [somePageSelected, allPageSelected]);

  const totalsBarMode = selectedTotals.count > 0 ? 'selected' : 'page';
  const totalsBarData = totalsBarMode === 'selected' ? selectedTotals : pageTotals;
  const totalsBarPageSuffix =
    filteredItems.length < (report?.items.length ?? 0) ? ' in view' : '';

  const selectItem = (item: Item | null) => {
    if (!item) {
      setFilterItemId(null);
      setFilterItemName('');
      return;
    }
    setFilterItemId(item.id);
    setFilterItemName(item.name);
  };

  const toggleLayers = async (item: BankCollateralStockItem) => {
    const itemId = item.item_id;
    const isExpanded = expandedLayers[itemId];

    if (isExpanded) {
      setExpandedLayers((prev) => ({ ...prev, [itemId]: false }));
      return;
    }

    setExpandedLayers((prev) => ({ ...prev, [itemId]: true }));

    if (layerData[itemId]) return;

    setLayerLoading((prev) => ({ ...prev, [itemId]: true }));
    setLayerErrors((prev) => {
      const next = { ...prev };
      delete next[itemId];
      return next;
    });

    try {
      const valuation = await getStockValuation({ item_id: itemId, include_layers: true });
      const layers = valuation.items[0]?.layers ?? [];
      setLayerData((prev) => ({ ...prev, [itemId]: layers }));
    } catch (err) {
      setLayerErrors((prev) => ({
        ...prev,
        [itemId]: err instanceof Error ? err.message : 'Failed to load FIFO layers',
      }));
    } finally {
      setLayerLoading((prev) => ({ ...prev, [itemId]: false }));
    }
  };

  const exportReport = () => {
    if (!report) return;

    const rows = [
      ['Bank Collateral Stock Report'],
      [`As of: ${report.as_of}`],
      [`Price basis: ${report.price_basis}`, `Valuation method: ${report.valuation_method}`],
      [],
      [
        'SKU',
        'Name',
        'UOM',
        'Qty on Hand',
        'Eligible Qty',
        'Avg Unit Cost',
        'Unit Selling Price',
        'Extended Cost',
        'Extended Retail',
        'Gross Margin',
        'Margin %',
      ],
      ...sortedItems.map((item) => [
        item.sku,
        item.name,
        item.unit_of_measure,
        item.qty_on_hand,
        item.eligible_qty,
        item.average_unit_cost,
        item.unit_selling_price,
        item.extended_cost,
        item.extended_retail,
        item.gross_margin,
        item.margin_percent,
      ]),
      [],
      [
        'Totals',
        '',
        '',
        '',
        report.totals.eligible_qty,
        '',
        '',
        report.totals.extended_cost,
        report.totals.extended_retail,
        report.totals.gross_margin,
        '',
      ],
    ];

    const csv = rows.map((row) => row.map((cell) => `"${cell}"`).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `bank-collateral-stock-${todayLocalIso()}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  };

  const filters = (
    <div className="flex flex-col gap-4">
      <div className="flex flex-wrap items-end gap-4">
        <div className="w-full sm:w-64">
          <label className="block text-xs font-medium text-gray-500 uppercase tracking-wide mb-1.5">
            Item
          </label>
          <AsyncItemSelect
            className="w-full"
            value={filterItemId ?? 0}
            selectedLabel={filterItemName}
            onSelect={selectItem}
            onClear={() => selectItem(null)}
            placeholder="All items"
            searchPlaceholder="Search by name or SKU..."
            itemType="INVENTORY"
          />
        </div>
        <label className="inline-flex items-center gap-2 text-sm text-gray-700 pb-2">
          <input
            type="checkbox"
            checked={activeOnly}
            onChange={(e) => setActiveOnly(e.target.checked)}
            className="h-4 w-4 rounded border-gray-300"
          />
          Active items only
        </label>
        <label className="inline-flex items-center gap-2 text-sm text-gray-700 pb-2">
          <input
            type="checkbox"
            checked={includeZeroQty}
            onChange={(e) => setIncludeZeroQty(e.target.checked)}
            className="h-4 w-4 rounded border-gray-300"
          />
          Include zero-qty items
        </label>
        <button
          type="button"
          onClick={runReport}
          disabled={loading}
          className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-slate-800 hover:bg-slate-700 rounded-lg disabled:opacity-60"
        >
          <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
          Run Report
        </button>
      </div>
      <div className="relative max-w-md">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 h-4 w-4" />
        <input
          type="text"
          placeholder="Search by SKU or name..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="w-full pl-10 pr-9 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-slate-800/20 focus:border-slate-400"
        />
        {searchQuery && (
          <button
            type="button"
            onClick={() => setSearchQuery('')}
            className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
          >
            <X className="h-4 w-4" />
          </button>
        )}
      </div>
    </div>
  );

  const tableColCount = 12;

  return (
    <ReportLayout
        title="Bank Collateral Stock"
        description="Point-in-time collateral snapshot for lender submissions. Cost from FIFO; retail from catalog unit price. Advance rates are applied by the lender offline."
        filters={filters}
        onBack={() => navigate('/reports')}
        backLabel="Back to Reports"
        onExport={report ? exportReport : undefined}
        onPrint={() => window.print()}
      >
        {loading ? (
          <div className="p-12 text-center">
            <Loader2 className="w-8 h-8 animate-spin mx-auto text-slate-400 mb-4" />
            <p className="text-slate-500 font-medium">Loading report data...</p>
          </div>
        ) : error ? (
          <div className="p-8 text-center">
            <AlertCircle className="w-10 h-10 mx-auto text-red-500 mb-4" />
            <p className="text-red-600 font-medium mb-4">{error}</p>
            <button
              type="button"
              onClick={runReport}
              className="text-sm text-slate-700 underline"
            >
              Retry
            </button>
          </div>
        ) : !report ? (
          <div className="p-8 text-center text-slate-500">No data available.</div>
        ) : (
          <div className="flex flex-col">
            <div className="p-4 md:p-6 border-b border-slate-100">
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 xl:grid-cols-7 gap-4">
                <div className="bg-slate-50 rounded-lg p-3">
                  <div className="flex items-center gap-2 text-xs text-gray-500 uppercase tracking-wide mb-1">
                    <Calendar className="h-3.5 w-3.5" />
                    As of
                  </div>
                  <p className="text-sm font-semibold text-gray-900">{formatReportDate(report.as_of)}</p>
                </div>
                <div className="bg-slate-50 rounded-lg p-3">
                  <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">Price basis</p>
                  <p className="text-sm font-semibold text-gray-900">{formatLabel(report.price_basis)}</p>
                </div>
                <div className="bg-slate-50 rounded-lg p-3">
                  <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">Method</p>
                  <p className="text-sm font-semibold text-gray-900">{report.valuation_method}</p>
                </div>
                <div className="bg-slate-50 rounded-lg p-3">
                  <div className="flex items-center gap-2 text-xs text-gray-500 uppercase tracking-wide mb-1">
                    <Package className="h-3.5 w-3.5" />
                    Eligible qty
                  </div>
                  <p className="text-sm font-semibold text-gray-900 font-mono">
                    {formatQuantity(report.totals.eligible_qty)}
                  </p>
                </div>
                <div className="bg-slate-50 rounded-lg p-3">
                  <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">Extended cost</p>
                  <p className="text-sm font-semibold text-gray-900 font-mono">
                    {formatCurrency(report.totals.extended_cost)}
                  </p>
                </div>
                <div className="bg-slate-50 rounded-lg p-3">
                  <div className="flex items-center gap-2 text-xs text-gray-500 uppercase tracking-wide mb-1">
                    <DollarSign className="h-3.5 w-3.5" />
                    Extended retail
                  </div>
                  <p className="text-sm font-semibold text-gray-900 font-mono">
                    {formatCurrency(report.totals.extended_retail)}
                  </p>
                </div>
                <div className="bg-slate-50 rounded-lg p-3">
                  <div className="flex items-center gap-2 text-xs text-gray-500 uppercase tracking-wide mb-1">
                    <TrendingUp className="h-3.5 w-3.5" />
                    Gross margin
                  </div>
                  <p
                    className={`text-sm font-semibold font-mono ${
                      parseFloat(report.totals.gross_margin) >= 0
                        ? 'text-green-600'
                        : 'text-red-600'
                    }`}
                  >
                    {formatCurrency(report.totals.gross_margin)}
                  </p>
                </div>
              </div>
            </div>

            <div className="overflow-x-auto">
              {sortedItems.length === 0 ? (
                <div className="py-12 text-center text-gray-500">
                  {searchQuery
                    ? 'No items match your search.'
                    : 'No eligible collateral stock for the selected filters.'}
                </div>
              ) : (
                <table className="w-full min-w-[1100px] text-sm text-left">
                  <thead className="text-xs text-slate-500 uppercase bg-slate-50 border-b border-slate-200">
                    <tr>
                      <th className="px-4 py-3 w-10">
                        <input
                          ref={selectAllCheckboxRef}
                          type="checkbox"
                          checked={allPageSelected}
                          onChange={(e) => toggleSelectAll(e.target.checked)}
                          className="h-4 w-4 rounded border-gray-300 text-slate-800"
                          aria-label="Select all visible rows"
                        />
                      </th>
                      <SortableTableHeader<SortColumn>
                        label="Item"
                        column="item"
                        currentSort={sortColumn}
                        sortDir={sortDir}
                        onSort={handleSort}
                      />
                      <th className="px-4 py-3 text-left">UOM</th>
                      <th className="px-4 py-3 text-right">Qty on hand</th>
                      <SortableTableHeader<SortColumn>
                        label="Eligible qty"
                        column="eligible_qty"
                        currentSort={sortColumn}
                        sortDir={sortDir}
                        onSort={handleSort}
                        align="right"
                      />
                      <th className="px-4 py-3 text-right">Avg unit cost</th>
                      <th className="px-4 py-3 text-right">Unit selling price</th>
                      <SortableTableHeader<SortColumn>
                        label="Extended cost"
                        column="extended_cost"
                        currentSort={sortColumn}
                        sortDir={sortDir}
                        onSort={handleSort}
                        align="right"
                      />
                      <SortableTableHeader<SortColumn>
                        label="Extended retail"
                        column="extended_retail"
                        currentSort={sortColumn}
                        sortDir={sortDir}
                        onSort={handleSort}
                        align="right"
                      />
                      <SortableTableHeader<SortColumn>
                        label="Gross margin"
                        column="gross_margin"
                        currentSort={sortColumn}
                        sortDir={sortDir}
                        onSort={handleSort}
                        align="right"
                      />
                      <SortableTableHeader<SortColumn>
                        label="Margin %"
                        column="margin_percent"
                        currentSort={sortColumn}
                        sortDir={sortDir}
                        onSort={handleSort}
                        align="right"
                      />
                      <th className="px-4 py-3 text-center">Layers</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {sortedItems.map((item) => {
                      const rowKey = bankCollateralRowKey(item);
                      const isSelected = selectedKeys.has(rowKey);
                      const qtyOnHand = parseFloat(item.qty_on_hand) || 0;
                      const eligibleQty = parseFloat(item.eligible_qty) || 0;
                      const grossMargin = parseFloat(item.gross_margin) || 0;
                      const isExpanded = expandedLayers[item.item_id];

                      return (
                        <React.Fragment key={item.item_id}>
                          <tr
                            className={`hover:bg-slate-50/80${isSelected ? ' bg-slate-50/60' : ''}`}
                          >
                            <td className="px-4 py-3 w-10">
                              <input
                                type="checkbox"
                                checked={isSelected}
                                onChange={(e) => toggleRow(rowKey, e.target.checked)}
                                className="h-4 w-4 rounded border-gray-300 text-slate-800"
                                aria-label={`Select ${item.name}`}
                              />
                            </td>
                            <td className="px-4 py-3">
                              <div className="font-medium text-gray-900">{item.name}</div>
                              <div className="text-xs text-gray-500 font-mono">{item.sku}</div>
                            </td>
                            <td className="px-4 py-3 text-gray-600">
                              {formatLabel(item.unit_of_measure)}
                            </td>
                            <td
                              className={`px-4 py-3 text-right font-mono ${
                                qtyOnHand < 0 ? 'text-red-600' : 'text-gray-900'
                              }`}
                            >
                              {formatQuantity(item.qty_on_hand)}
                            </td>
                            <td
                              className={`px-4 py-3 text-right font-mono ${
                                eligibleQty === 0 ? 'text-gray-400' : 'text-gray-900'
                              }`}
                            >
                              {formatQuantity(item.eligible_qty)}
                            </td>
                            <td className="px-4 py-3 text-right font-mono text-gray-900">
                              {formatCurrency(item.average_unit_cost)}
                            </td>
                            <td className="px-4 py-3 text-right font-mono text-gray-900">
                              {formatCurrency(item.unit_selling_price)}
                            </td>
                            <td className="px-4 py-3 text-right font-mono text-gray-900">
                              {formatCurrency(item.extended_cost)}
                            </td>
                            <td className="px-4 py-3 text-right font-mono text-gray-900">
                              {formatCurrency(item.extended_retail)}
                            </td>
                            <td
                              className={`px-4 py-3 text-right font-mono ${
                                grossMargin >= 0 ? 'text-green-600' : 'text-red-600'
                              }`}
                            >
                              {formatCurrency(item.gross_margin)}
                            </td>
                            <td className="px-4 py-3 text-right font-mono text-gray-700">
                              {formatMarginPercent(item.margin_percent, item.extended_retail)}
                            </td>
                            <td className="px-4 py-3 text-center">
                              <button
                                type="button"
                                onClick={() => void toggleLayers(item)}
                                className="inline-flex items-center gap-1 text-sm text-slate-700 hover:text-slate-900"
                              >
                                <Layers className="h-4 w-4" />
                                {isExpanded ? 'Hide' : 'View'}
                              </button>
                            </td>
                          </tr>
                          {isExpanded && (
                            <tr>
                              <td colSpan={tableColCount} className="px-6 py-4 bg-gray-50">
                                {layerLoading[item.item_id] ? (
                                  <div className="flex items-center gap-2 text-sm text-gray-500">
                                    <Loader2 className="h-4 w-4 animate-spin" />
                                    Loading FIFO layers...
                                  </div>
                                ) : layerErrors[item.item_id] ? (
                                  <p className="text-sm text-red-600">{layerErrors[item.item_id]}</p>
                                ) : (layerData[item.item_id]?.length ?? 0) === 0 ? (
                                  <p className="text-sm text-gray-500">No FIFO layers for this item.</p>
                                ) : (
                                  <div className="overflow-x-auto">
                                    <h4 className="font-medium text-gray-900 text-sm mb-2">
                                      FIFO Layers
                                    </h4>
                                    <table className="w-full min-w-[600px] text-sm">
                                      <thead>
                                        <tr className="text-left text-xs text-gray-500 uppercase">
                                          <th className="pb-2 pr-4">Date acquired</th>
                                          <th className="pb-2 pr-4 text-right">Qty remaining</th>
                                          <th className="pb-2 pr-4 text-right">Unit cost</th>
                                          <th className="pb-2 text-right">Layer value</th>
                                        </tr>
                                      </thead>
                                      <tbody className="divide-y divide-gray-200">
                                        {layerData[item.item_id]?.map((layer) => (
                                          <tr key={layer.layer_id}>
                                            <td className="py-2 pr-4 text-gray-700">
                                              {formatReportDate(layer.date_acquired)}
                                            </td>
                                            <td className="py-2 pr-4 text-right font-mono text-gray-900">
                                              {formatQuantity(layer.qty_remaining)}
                                            </td>
                                            <td className="py-2 pr-4 text-right font-mono text-gray-900">
                                              {formatCurrency(layer.unit_cost)}
                                            </td>
                                            <td className="py-2 text-right font-mono font-medium text-gray-900">
                                              {formatCurrency(layer.layer_value)}
                                            </td>
                                          </tr>
                                        ))}
                                      </tbody>
                                    </table>
                                  </div>
                                )}
                              </td>
                            </tr>
                          )}
                        </React.Fragment>
                      );
                    })}
                  </tbody>
                  <tfoot className="bg-slate-50 border-t border-slate-200">
                    <tr>
                      <td colSpan={4} className="px-4 py-3 font-semibold text-slate-900 text-right">
                        Report totals
                      </td>
                      <td className="px-4 py-3 text-right font-semibold font-mono text-slate-900">
                        {formatQuantity(report.totals.eligible_qty)}
                      </td>
                      <td colSpan={2} />
                      <td className="px-4 py-3 text-right font-semibold font-mono text-slate-900">
                        {formatCurrency(report.totals.extended_cost)}
                      </td>
                      <td className="px-4 py-3 text-right font-semibold font-mono text-slate-900">
                        {formatCurrency(report.totals.extended_retail)}
                      </td>
                      <td
                        className={`px-4 py-3 text-right font-semibold font-mono ${
                          parseFloat(report.totals.gross_margin) >= 0
                            ? 'text-green-600'
                            : 'text-red-600'
                        }`}
                      >
                        {formatCurrency(report.totals.gross_margin)}
                      </td>
                      <td colSpan={2} />
                    </tr>
                  </tfoot>
                </table>
              )}
            </div>

            {sortedItems.length > 0 && (
              <InventoryMovementTotalsBar
                mode={totalsBarMode}
                count={totalsBarData.count}
                qtyChange={totalsBarData.eligibleQty}
                valueChange={totalsBarData.extendedRetail}
                qtyLabel="Eligible qty"
                valueLabel="Extended retail"
                pageCountSuffix={totalsBarPageSuffix}
                onClear={totalsBarMode === 'selected' ? clearSelection : undefined}
              />
            )}
          </div>
        )}
      </ReportLayout>
  );
};

export default BankCollateralStock;
